import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Toolbar,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { signedInUser, useSignedInUserState } from "../../hooks/useAuthState";
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import GpsButtonControl from "./gpsButtonControl";
import { toast } from "react-toastify";

export interface Coordinates {
  lat: number;
  lng: number;
}

const MapCenterTracker = ({ setPosition }: { setPosition: (pos: Coordinates) => void }) => {
  const map = useMap();

  useEffect(() => {
    const onMove = () => {
      const center = map.getCenter();
      setPosition({ lat: center.lat, lng: center.lng });
    };

    map.on("move", onMove);
    return () => {
      map.off("move", onMove);
    };
  }, [map, setPosition]);

  return null;
};

export const LOCAL_STORAGE_KEY = "localLocation";
export const LOCAL_STORAGE_TTL_KEY = "localLocationTtl";
export const HAS_VISITED = "hasVisited"

export default function MapDialog({
  open,
  onClose,
  onConfirm,
  user,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (coords: Coordinates) => void;
  user: signedInUser;
}) {
  const { lat, lng } = useParams();
  const [position, setPosition] = useState<Coordinates | null>(null);
  const [leafletMap, setLeafletMap] = useState<L.Map | null>(null);
  const isGpsUsedRef = useRef(false);
  const [loading, setLoading] = useState<boolean>(false);
  const isFlutterWebView = () => { return !!(window as any).isFlutterWebView };

  useEffect(() => {
    if (open) {
      loadLocation();
    }
  }, [open]);

  const pendingCoords = useRef<Coordinates | null>(null);

  useEffect(() => {
    (window as any).onFlutterLocation = (lat: number, lng: number) => {
      if (!leafletMap) {
        pendingCoords.current = { lat, lng };
        return;
      }
      updateLocation({ lat, lng });
    };
  }, [leafletMap]);

  useEffect(() => {
    if (leafletMap && pendingCoords.current) {
      updateLocation(pendingCoords.current);
      pendingCoords.current = null;
    }
  }, [leafletMap]);

  const sendFlutterMessage = (message: string, data?: any) => {
    if ((window as any).isFlutterWebView && (window as any).FlutterBridge) {
      (window as any).FlutterBridge.postMessage(
        JSON.stringify({ message, data })
      );
    }
  };

  const tryGetLocationFromGPS = () => {
    return new Promise<Coordinates>((resolve, reject) => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };
            resolve(coords);
          },
          (error) => {
            console.warn("Geolocation error:", error.message);
            reject(error);
          },
          { enableHighAccuracy: true, timeout: 10000 } // 10s timeout
        );
      } else {
        reject(new Error("Geolocation not supported"));
      }
    });
  };

  const loadLocation = async () => {
    const storedLocation = localStorage.getItem(LOCAL_STORAGE_KEY);
    try {
      setLoading(true);
      if (isGpsUsedRef.current) {
        isGpsUsedRef.current = false;

        const gpsCoords = await tryGetLocationFromGPS();
        updateLocation(gpsCoords);

      } else if (storedLocation) {
        updateLocation(JSON.parse(storedLocation));

      } else if (
        user?.profile?.addressDetails?.lat &&
        user?.profile?.addressDetails?.lng
      ) {
        updateLocation({
          lat: user.profile.addressDetails.lat,
          lng: user.profile.addressDetails.lng,
        });
      } else {
        updateLocation({ lat: -33.9249, lng: 18.4241 });
      }

      toast.success("Location found");
    } catch (error) {
      console.error(error);
      toast.error("Error during location retrieval");
      updateLocation({ lat: -33.9249, lng: 18.4241 });
    } finally {
      setLoading(false);
    }
  };

  const updateLocation = (coords: Coordinates, fly: boolean = true) => {
    setPosition(coords);

    console.log("LEAFLET" + leafletMap);
    console.log("LEAFLET CONTAINER" + leafletMap?.getContainer);
    console.log("OFFSET PARENT" + leafletMap?.getContainer()?.offsetParent);

    if (
      leafletMap &&
      leafletMap.getContainer &&
      leafletMap.getContainer()?.offsetParent !== null
    ) {
      if (fly) {
        console.log("animating");
        leafletMap.flyTo(coords, 15, { animate: true });
      } else {
        console.log("animating");
        leafletMap.setView(coords, 15); // or just update marker if you prefer
      }
    }
  };

  const handleGpsClick = () => {
    // Mobile
    if ((window as any).isFlutterWebView) {
      sendFlutterMessage("getDeviceLocation")
    } else {
      // Browser
      isGpsUsedRef.current = true;
      loadLocation();
    }
  };


  const handleConfirm = () => {
    if (!position || position.lat === 0 || position.lng === 0) {
      alert("Error: Could not determine your location. Please try using GPS again.");
      return;
    }

    const now = new Date();
    const expireAt = new Date(now.getTime() + 20 * 60 * 1000) // Expire Location in 20 min
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(position));
    localStorage.setItem(LOCAL_STORAGE_TTL_KEY, expireAt.toISOString());
    localStorage.setItem(HAS_VISITED, "true");
    window.dispatchEvent(new CustomEvent("locationUpdated", { detail: position }));
    isGpsUsedRef.current = false;
    toast.info("Location confirmed");
    onConfirm(position);
  };

  const MapRefSetter = ({ setMap }: { setMap: (map: L.Map) => void }) => {
    const map = useMap();

    useEffect(() => {
      setMap(map);
    }, [map]);

    return null;
  };

  return (
    <Dialog open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      fullWidth maxWidth="md"
    >
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <Typography variant="h6" style={{ flexGrow: 1, textAlign: "center" }}>
            Please Confirm Your Location
          </Typography>
        </Toolbar>
      </AppBar>
      <DialogContent style={{ height: "400px", padding: 0, position: "relative" }}>
        {!position ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        ) : (
          <MapContainer center={position}
            zoom={15}
            style={{ width: "100%", height: "100%" }}
          >
            <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            <MapRefSetter setMap={setLeafletMap} />
            <MapCenterTracker setPosition={setPosition} />
            <GpsButtonControl onClick={handleGpsClick} />
          </MapContainer>
        )}

        {/* Floating marker centered over map */}
        <img
          src="/map_assets/Marker.svg"
          alt="center marker"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: "80px",
            height: "80px",
            transform: "translate(-50%, -100%)", // center and lift anchor
            pointerEvents: "none",
            zIndex: 1000,
          }}
        />
      </DialogContent>
      <DialogActions style={{ justifyContent: "space-between" }}>
        <div>
          {/* Conditionally render the button based on loading state */}
          {!loading && (
            <Button
              onClick={onClose}
              variant="outlined"
              sx={{ margin: "0 8px", border: "1px solid #162C1F", borderRadius: "24px" }}
            >
              <Typography color={"#162C1F"}>Close</Typography>
            </Button>
          )}

          {loading && <></>}
        </div>

        <div>
          {!loading && (
            <Button
              variant="contained"
              sx={{
                margin: "0 8px",
                background: "#162C1F",
                border: "1px solid #162C1F",
                borderRadius: "24px",
              }}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          )}
          {loading && <></>}
        </div>
      </DialogActions>
    </Dialog>
  );
}
